import React, { useRef, useState } from 'react';

import { cn } from '@easydatasolution/ui-react/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import GoogleReCAPTCHA from 'react-google-recaptcha';
import { SubmitHandler, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as z from 'zod';

import { useMail } from '../../hooks/use-mail';
import { useToast } from '../../hooks/use-toast';
import { createMessage, createName } from '../../misc/contact';
import { Button } from '../Button/Button';
import { FileUpload } from '../FileUpload/FileUpload';
import { FormCheckboxPrivacy } from '../FormCheckbox/FormCheckboxPrivacy/FormCheckboxPrivacy';
import { FormInput } from '../FormInput/FormInput';
import { FormSelect } from '../FormSelect/FormSelect';
import { FormTextarea } from '../FormTextarea/FormTextarea';
import { ReCAPTCHA } from '../ReCAPTCHA/ReCAPTCHA';

// valid as SSR is disabled for using FileList
const isServer = typeof window === 'undefined';

const ANREDE_OPTIONS = [
	{ value: 'Herr', text: 'Herr' },
	{ value: 'Frau', text: 'Frau' },
	{ value: 'Divers', text: 'Divers' },
] as const;

const AUFMERKSAM_OPTIONS = [
	{
		value: 'google',
		text: 'Google',
	},
	{
		value: 'kursnet',
		text: 'Kursnet',
	},
	{
		value: 'wisy',
		text: 'Wisy - Hamburgs Kursportal',
	},
	{
		value: 'seminarmarkt',
		text: 'Seminarmarkt',
	},
	{
		value: 'seminarboerse',
		text: 'Seminarbörse',
	},
	{
		value: 'anderes_weiterbildungsportal',
		text: 'anderes Weiterbildungsportal',
	},
	{
		value: 'empfehlung_berufliches_umfeld',
		text: 'Empfehlung aus dem beruflichen Umfeld',
	},
	{
		value: 'empfehlung_privates_umfeld',
		text: 'Empfehlung aus dem privaten Umfeld',
	},
	{
		value: 'flyer',
		text: 'Flyer',
	},
	{
		value: 'elektronischer_newsletter',
		text: 'elektronischer Newsletter',
	},
	{
		value: 'sonstiges',
		text: 'Sonstiges',
	},
] as const;

const ANHANG_MAX_MB = 10;

// NOTE: fields have empty strings as default values, required fields allow empty strings -> use min
const contactFormSchema = z.object({
	interesse: z.string().min(1, { message: 'Interesse ist ein Pflichtfeld.' }),
	anrede: z
		.enum(
			['' as any, ...ANREDE_OPTIONS.map((option) => option.value)] as [
				(typeof ANREDE_OPTIONS)[number]['value'],
				...(typeof ANREDE_OPTIONS)[number]['value'][],
			],
			{ invalid_type_error: 'Anrede ist ein Pflichtfeld.' },
		)
		.transform((value) => value || undefined)
		.optional(),
	vorname: z
		.string()
		.max(100, { message: 'Vorname kann nicht länger als 100 Zeichen sein.' })
		.optional(),
	nachname: z
		.string()
		.min(1, { message: 'Nachname ist ein Pflichtfeld.' })
		.max(100, { message: 'Nachname kann nicht länger als 100 Zeichen sein.' }),
	email: z.string().email({ message: 'Bitte geben Sie eine gültige E-Mail Adresse ein.' }),
	telefon: z
		.string()
		.max(20, { message: 'Telefonnummer kann nicht länger als 20 Zeichen sein.' })
		.optional(),
	firma: z
		.string()
		.max(100, { message: 'Firma kann nicht länger als 100 Zeichen sein.' })
		.optional(),
	strasse: z
		.string()
		.max(100, { message: 'Straße kann nicht länger als 100 Zeichen sein.' })
		.optional(),
	plz: z.string().max(10, { message: 'PLZ kann nicht länger als 10 Zeichen sein.' }).optional(),
	ort: z.string().max(100, { message: 'Ort kann nicht länger als 100 Zeichen sein.' }).optional(),
	land: z.string().max(100, { message: 'Land kann nicht länger als 100 Zeichen sein.' }).optional(),
	aufmerksam: z
		.enum(['' as any, ...AUFMERKSAM_OPTIONS.map((option) => option.value)] as [
			(typeof AUFMERKSAM_OPTIONS)[number]['value'],
			...(typeof AUFMERKSAM_OPTIONS)[number]['value'][],
		])
		.transform((value) => value || undefined)
		.optional(),
	nachricht: z
		.string()
		.min(1, { message: 'Nachricht ist ein Pflichtfeld.' })
		.max(1000, { message: 'Anmerkung kann nicht länger als 1000 Zeichen sein.' }),
	// bundling fails otherwise
	// see: https://github.com/orgs/react-hook-form/discussions/11096#discussioncomment-11371112
	anhang: isServer
		? z.any()
		: z
				.instanceof(FileList)
				.refine(
					(files) => {
						if (!files) return true;

						const actualTotalMb = bytesToMb(
							Array.from(files)
								.map((file) => file.size)
								.reduce((prev, curr) => prev + curr, 0),
						);
						return actualTotalMb <= ANHANG_MAX_MB;
					},
					{
						message: `Anhang kann nicht größer als ${ANHANG_MAX_MB}MB sein.`,
					},
				)
				.optional(),
	datenschutz: z.boolean().refine((data) => data, {
		message: 'Datenschutz ist ein Pflichtfeld.',
	}),
});
type ContactFormSchema = z.infer<typeof contactFormSchema>;

function bytesToMb(bytes: number) {
	return bytes / (1000 * 1000);
}

type ContactFormProps = {
	defaultValues?: Partial<ContactFormSchema>;
	onSuccess?: () => void;
	className?: string;
};

export const ContactForm = ({ className, defaultValues, onSuccess }: ContactFormProps) => {
	const { showSuccessToast, showErrorToast } = useToast();
	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitted, isDirty },
		reset,
	} = useForm<ContactFormSchema>({
		shouldFocusError: true,
		resolver: zodResolver(contactFormSchema),
		defaultValues,
	});

	const { isSending, sendMail } = useMail();

	const [isRecaptchaCompleted, setIsRecaptchaCompleted] = useState(false);
	const recaptchaRef = useRef<GoogleReCAPTCHA>(null);

	const onSubmit: SubmitHandler<ContactFormSchema> = async (data) => {
		if (!data || !isRecaptchaCompleted) return;

		try {
			const { anhang, datenschutz, ...messageData } = data;

			const name = createName(data.vorname, data.nachname);
			await sendMail({
				replyToMail: data.email,
				subject: `[Kontaktformular] Anfrage von ${name}, ${uuidv4()}`,
				message: createMessage({
					...messageData,
				}),
				attachments: anhang,
				recaptchaToken: recaptchaRef.current!.getValue(),
				verifyRecaptcha: true,
			});
			onSuccess?.();
			reset();
			showSuccessToast({
				title: 'Kontaktanfrage erfolgreich',
				message: 'Wir werden uns mit Ihnen in Verbindung setzen.',
			});
		} catch (e) {
			showErrorToast({
				message: 'Bitte probieren Sie es erneut oder kontaktieren Sie uns auf anderem Wege.',
			});
		}
	};

	return (
		<form className={cn('grid grid-cols-12 gap-4', className)} onSubmit={handleSubmit(onSubmit)}>
			<FormInput
				label="Wofür interessieren Sie sich?"
				error={errors.interesse}
				required
				{...register('interesse')}
				className="col-span-12 lg:col-span-6"
			/>
			<FormSelect
				label="Wie haben Sie uns gefunden?"
				placeholder="auswählen"
				error={errors.aufmerksam}
				{...register('aufmerksam')}
				className="col-span-12 text-secondary-700 lg:col-span-6"
			>
				{AUFMERKSAM_OPTIONS.map((option) => (
					<option key={option.value} value={option.value}>
						{option.text}
					</option>
				))}
			</FormSelect>

			<div className="col-span-12 mt-3 flex flex-col gap-2">
				<h3 className="col-span-12 text-xl font-semibold text-secondary-700">
					Persönliche Details
				</h3>
				<div className="grid grid-cols-12 gap-4">
					<FormSelect
						label="Anrede"
						placeholder="auswählen"
						error={errors.anrede}
						{...register('anrede')}
						className="col-span-12 text-secondary-700 lg:col-span-2"
					>
						{ANREDE_OPTIONS.map((option) => (
							<option key={option.value} value={option.value}>
								{option.text}
							</option>
						))}
					</FormSelect>
					<FormInput
						label="Vorname"
						error={errors.vorname}
						{...register('vorname')}
						className="col-span-12 lg:col-span-4"
					/>
					<FormInput
						label="Nachname"
						error={errors.nachname}
						required
						{...register('nachname')}
						className="col-span-12 lg:col-span-6"
					/>
					<FormInput
						label="E-Mail"
						error={errors.email}
						required
						type="email"
						{...register('email')}
						className="col-span-12 lg:col-span-6"
					/>
					<FormInput
						label="Telefon"
						error={errors.telefon}
						{...register('telefon')}
						className="col-span-12 lg:col-span-6"
					/>
				</div>
			</div>

			<div className="col-span-12 mt-3 flex flex-col gap-2">
				<h3 className="col-span-12 text-xl font-semibold text-secondary-700">Adresse</h3>
				<div className="grid grid-cols-12 gap-4">
					<FormInput
						label="Firma"
						error={errors.firma}
						{...register('firma')}
						className="col-span-12 lg:col-span-6"
					/>
					<FormInput
						label="Straße"
						error={errors.strasse}
						{...register('strasse')}
						className="col-span-12 lg:col-span-6"
					/>
					<FormInput
						label="PLZ"
						error={errors.plz}
						{...register('plz')}
						className="col-span-12 lg:col-span-2"
					/>
					<FormInput
						label="Ort"
						error={errors.ort}
						{...register('ort')}
						className="col-span-12 lg:col-span-4"
					/>
					<FormInput
						label="Land"
						error={errors.land}
						{...register('land')}
						className="col-span-12 lg:col-span-6"
					/>
				</div>
			</div>

			<div className="col-span-12 mt-3 flex flex-col gap-2">
				<h3 className="col-span-12 text-xl font-semibold text-secondary-700">Inhalt</h3>
				<div className="grid grid-cols-12 gap-4">
					<FormTextarea
						label="Nachricht"
						error={errors.nachricht}
						required
						helperText="Nennen Sie uns gerne auch Ihren Wunsch-Termin."
						{...register('nachricht')}
						className="col-span-12"
					/>
					<FileUpload
						label="Anhang"
						helperText={`max. ${ANHANG_MAX_MB}MB`}
						error={errors.anhang}
						{...register('anhang')}
						className="col-span-12 text-secondary-700"
					/>
				</div>
			</div>

			<FormCheckboxPrivacy
				error={errors.datenschutz}
				{...register('datenschutz')}
				className="col-span-12"
			/>

			<div className="col-span-12 flex flex-col items-center justify-between gap-4 md:flex-row md:items-end">
				<ReCAPTCHA
					ref={recaptchaRef}
					setIsCompleted={setIsRecaptchaCompleted}
					showError={isSubmitted && !isRecaptchaCompleted}
					className="scale-[0.8] md:scale-100"
				/>
				{isSubmitted && !isRecaptchaCompleted && (
					<p className="mt-1 text-sm text-red-500">Das reCAPTCHA wird benötigt.</p>
				)}

				<Button
					isDisabled={!isDirty}
					justifySelf={{ base: undefined, md: 'start' }}
					isLoading={isSending}
					type="submit"
				>
					Kostenlos anfragen
				</Button>
			</div>
		</form>
	);
};
